// src/components/itinerary/ActivityCard.tsx
// src/components/itinerary/ActivityCard.tsx
// Update the ActivityCard component to have fixed heights and better drag behavior

import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Chip, 
  IconButton 
} from '@mui/material';
import { 
  Schedule as ScheduleIcon,
  DirectionsCar as TransportIcon,
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon,
  Attractions as AttractionIcon,
  Edit as EditIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import { Activity } from '../../contexts/ItineraryContext';

interface ActivityCardProps {
  activity: Activity;
  isDragging: boolean;
  dragHandleProps: any;
  onEdit: (activity: Activity) => void;
}

const ActivityCard: React.FC<ActivityCardProps> = ({ 
  activity, 
  isDragging, 
  dragHandleProps, 
  onEdit 
}) => {
  // Get icon for activity type
  const getActivityIcon = (type: string) => {
    switch(type) {
      case 'activity':
        return <AttractionIcon color="primary" />;
      case 'food':
        return <RestaurantIcon color="secondary" />;
      case 'accommodation':
        return <HotelIcon color="action" />;
      case 'transportation':
        return <TransportIcon color="info" />;
      default:
        return <ScheduleIcon />;
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        height: '130px', // Fixed height to prevent layout shifts
        transform: isDragging ? 'rotate(3deg) scale(1.02)' : 'none',
        boxShadow: isDragging ? 3 : 1,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        position: 'relative',
        zIndex: isDragging ? 1200 : 1,
        cursor: isDragging ? 'grabbing' : 'default'
      }}
    >
      <CardContent sx={{ 
        p: 2, 
        '&:last-child': { pb: 2 },
        height: '100%',
        overflow: 'hidden' // Prevent content from overflowing
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start',
          height: '100%'
        }}>
          <Box {...dragHandleProps} sx={{ 
            mr: 1, 
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
            display: 'flex',
            alignItems: 'center',
            height: '100%'
          }}>
            <DragIcon color="action" />
          </Box>
          
          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              {getActivityIcon(activity.type)}
              <Typography variant="subtitle1" sx={{ ml: 1 }} noWrap>
                {activity.title}
              </Typography>
            </Box>
            
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: '1.2em',
                maxHeight: '2.4em' // Limit to 2 lines
              }}
            >
              {activity.description}
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 1, 
              flexWrap: 'wrap', 
              gap: 0.5,
              maxHeight: '24px', // Limit height of chip container
              overflow: 'hidden'
            }}>
              <Chip 
                label={activity.type.charAt(0).toUpperCase() + activity.type.slice(1)} 
                size="small" 
              />
              {activity.cost !== undefined && (
                <Chip label={`$${activity.cost}`} size="small" />
              )}
              {activity.duration && (
                <Chip label={activity.duration} size="small" />
              )}
            </Box>
            
            <Typography variant="body2" sx={{ mt: 1 }} noWrap>
              {activity.location}
            </Typography>
          </Box>
          
          <IconButton 
            size="small" 
            onClick={() => onEdit(activity)}
            sx={{ 
              ml: 1,
              position: 'absolute',
              top: 8,
              right: 8
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ActivityCard;